// ─────────────────────────────────────────────
//  ImportRunSummary — what the unattended import did last night, and what it
//  needs a person for before 06:00.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  ⚠️ THIS IS WHERE THE NIGHT'S REFUSALS SURFACE. The scheduled import
//  FULL-RESETS every board at 00:30, so a name it could not identify rebuilds
//  the day without that person — and until now its only report of that was a
//  skip list rendered on a screen the job never touched. The queue below is
//  written onto the `import_runs` record by the job itself, and this panel is
//  the first thing an admin opens in the morning.
//
//  ⚠️ NOBODY IN THIS LIST WAS CREATED. Every line is a person the import
//  refused to invent — the previous behaviour minted 22 of them in one night.
//  Product owner, 2026-08-20: "never create — always ask a human."
//
//  Presentational only: the parent hands over one `import_runs` document.
// ─────────────────────────────────────────────
import React from "react";
import tokens from "../theme/tokens";

const { color } = tokens;

// How many questions to print before collapsing to a count. A morning reader
// scanning a settings panel is triaging, not clearing — the queue itself lives
// in the importer.
const MAX_SHOWN = 12;

const TONE = {
  ok: { bg: "#10b98114", fg: "#0a7d5e", border: "#10b98166" },
  partial: { bg: "#f59e0b18", fg: "#7a4f00", border: "#f59e0b" },
};

export default function ImportRunSummary({ run }) {
  if (!run) return null;
  const tone = TONE[run.status] || { bg: "#fee2e2", fg: color.accent.error, border: color.accent.error };
  const queue = run.reviewQueue || [];

  return (
    <>
      <div
        role="status"
        style={{
          fontSize: 12, fontWeight: 700, marginBottom: 10, padding: "6px 10px", borderRadius: 6,
          background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}`,
        }}
      >
        Last automated import — {run.dateKey}{" "}
        ({run.source === "internal" ? "internal schedule" : "Google Sheet"}):{" "}
        {String(run.status || "").toUpperCase()}
        {Object.entries(run.perFacility || {})
          .map(([fid, r]) => ` · ${fid}: ${r.ok ? `${r.rooms} rooms` : r.reason || r.error || "failed"}`)
          .join("")}
        {run.error ? ` · ${run.error}` : ""}
      </div>

      {run.coverage && (
        <div style={st.coverage}>
          {/* The same sentence the manual importer shows and the job logs. */}
          <div role="status" style={st.line}>{run.coverage.line}</div>

          {(run.coverage.unknownDoctorCodes || []).length > 0 && (
            <div style={st.note}>
              Attending codes no doctor record carries:{" "}
              <strong>{run.coverage.unknownDoctorCodes.join(", ")}</strong>.
            </div>
          )}

          {queue.length > 0 && (
            <>
              <div style={st.note}>
                Nobody below was created. Confirm each in the Daily List's import
                review, or fix the cell in the sheet and re-import.
              </div>
              <ul style={st.list}>
                {queue.slice(0, MAX_SHOWN).map((i) => (
                  <li key={i.id} style={st.item}>
                    <strong>{i.name || i.verbatim}</strong>
                    {i.room ? ` · ${i.room}` : ""}
                    {i.candidates?.length
                      ? ` — closest on file: ${i.candidates.map((c) => c.name).join(", ")}`
                      : " — nobody on file resembles this name"}
                  </li>
                ))}
              </ul>
              {queue.length > MAX_SHOWN && (
                <div style={st.note}>
                  +{queue.length - MAX_SHOWN} more{run.reviewQueueTruncated ? " (list capped by the run record)" : ""}.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

const st = {
  coverage: {
    marginBottom: 10, padding: "8px 10px", borderRadius: 6,
    border: `1px solid ${color.border.default}`, background: color.surface.raised,
  },
  line: { fontSize: 13, fontWeight: 800, color: color.text.primary },
  note: { fontSize: 11, color: color.text.muted, margin: "4px 0 6px" },
  list: { margin: 0, paddingLeft: 16, fontSize: 11, color: color.text.secondary },
  item: { marginBottom: 2 },
};
